import { LiveAsset, LiveAssetDetails, LiveAssetProperty, LiveAssetStatus } from "@rvi/asset-modules";
import { forkJoin, map, Observable, of } from "rxjs";
import { SubjectDetail } from "../../_objects/subject";
import { RectInStructImage, SubjectLocation, SubjectModelDetail } from "../../_objects/subject-model";
import { SubjectAttributesSchema } from "../../_offline/models/subject-attributes.schema";
import { SubjectSchema } from "../../_offline/models/subject.schema";
import { SubjectModelLocationsSchema } from "../../_offline/models/subjectModel-locations.schema";
import { SubjectModelSchema } from "../../_offline/models/subjectModel.schema";

type GetImage = (assetTypeId: string) => Observable<string | null>;
type GetProperties = (liveAssetId: string) => Observable<LiveAssetProperty[]>;
export type LabelConfig = Partial<{ Owner: string; Country: string; State: string; Region: string; Site: string; Project: string }>;

export class LiveAssetMapper {
  private static dataPlate: string = "dataplate";

  private static separator = "_";

  private static readonly SystemProperty = {
    SerialNumber: "SerialNumber",
    Owner: "Owner",
    Country: "Country",
    State: "State",
    Region: "Region",
    Site: "Site",
    Project: "Project"
  } as const;

  public static fromLiveAssetToSubject(liveAsset: LiveAsset, tenantId: string): Observable<SubjectSchema> {
    return this.buildSchema(liveAsset, [], tenantId);
  }

  public static fromLiveAssetDetailsToSubject(
    live: LiveAssetDetails,
    tenantId: string,
    getImage?: GetImage,
    getProperties?: GetProperties,
    labelConfig?: LabelConfig
  ): Observable<SubjectSchema> {
    return this.buildSchema(live, live.children, tenantId, getImage, getProperties, labelConfig);
  }

  public static toSubjectModelFromLiveAsset(liveAsset: LiveAsset, children: LiveAssetDetails[], tenantId: string): Observable<SubjectModelSchema> {
    return this.buildSubjectModel(liveAsset, children, tenantId);
  }

  private static buildSchema(
    liveAsset: LiveAsset | LiveAssetDetails,
    children: LiveAssetDetails[],
    tenantId: string,
    getImage?: GetImage,
    getProperties?: GetProperties,
    labelConfig?: LabelConfig
  ): Observable<SubjectSchema> {
    const rootProperties$ = getProperties ? getProperties(liveAsset.id) : of([] as LiveAssetProperty[]);

    return forkJoin({
      model: this.buildSubjectModel(liveAsset, children, tenantId, getImage, getProperties),
      rootProperties: rootProperties$
    }).pipe(
      map(({ model, rootProperties }) => {
        const subject = this.toSubjectDetail(liveAsset, rootProperties, labelConfig);
        const schema = new SubjectSchema(tenantId, subject);
        schema.subjectAttributes = [new SubjectAttributesSchema(subject.id, 1, null, null, null, null, null, null, true)];
        schema.subjectModel = model;
        schema.subjectModelId = model.id;
        schema.assetTypeId = liveAsset.assetType?.id;
        return schema;
      })
    );
  }

  private static forkJoinOrEmpty<T>(list: Observable<T>[]) {
    return list.length > 0 ? forkJoin(list) : of([]);
  }

  private static buildSubjectModel(
    liveAsset: LiveAsset | LiveAssetDetails,
    children: LiveAssetDetails[],
    tenantId: string,
    getImage?: GetImage,
    getProperties?: GetProperties
  ): Observable<SubjectModelSchema> {
    const locations = children.map((child) => this.toSubjectLocation(child, "", getImage, getProperties));
    const image$ = getImage && liveAsset.assetType.structureImagePath ? getImage(liveAsset.assetType.id) : of(undefined);

    return forkJoin({ image: image$, locations: this.forkJoinOrEmpty(locations) }).pipe(
      map(({ image, locations }) => {
        const model = new SubjectModelDetail();
        model.id = liveAsset.id;
        model.name = liveAsset.name;
        model.isValid = true;
        model.isNewestVersion = true;
        model.locations = locations;
        model.version = 1;

        model.setFolderNameOfLocations(locations, 1);

        const modelSchema = new SubjectModelSchema(model, tenantId);
        modelSchema.structImageFilePath = image;
        modelSchema.subjectModelLocations = [new SubjectModelLocationsSchema(model.id, 1, locations, true)];
        return modelSchema;
      })
    );
  }

  private static toSubjectLocation(
    child: LiveAssetDetails,
    previousIdentifier: string,
    getImage?: GetImage,
    getProperties?: GetProperties
  ): Observable<SubjectLocation> {
    const name = `${child.displayName?.trim() || child.name} ${child.relation?.label ?? ""}`;
    const currentId = `${child.shortName}${child.relation?.label ?? ""}`;
    const uniqueId = previousIdentifier ? `${previousIdentifier}${this.separator}${currentId}` : currentId;

    const childrenLocations = child.children.map((c) => this.toSubjectLocation(c, uniqueId, getImage, getProperties));
    const image$ = getImage && child.assetType.structureImagePath ? getImage(child.assetType.id) : of(null);
    const properties$ = getProperties ? getProperties(child.id) : of([] as LiveAssetProperty[]);

    return forkJoin({ image: image$, children: this.forkJoinOrEmpty(childrenLocations), properties: properties$ }).pipe(
      map(({ image, children, properties }) => {
        const location = new SubjectLocation();
        location.abbreviation = child.shortName;
        location.folderName = name;
        location.name = name;
        location.identifier = uniqueId;
        location.assetTypeId = child.assetType.id;
        location.liveAssetId = child.id;
        location.structImagePath = image;
        location.children = children;
        location.rectInStructImage = this.toRectInStructImage(child.relation?.location);
        location.inspectionFor = properties.map((p) => `${p.name}: ${p.value}`);
        location.isOperational = child.status === LiveAssetStatus.Operational;
        location.isDataPlate = child.assetType.category?.name.toLowerCase() === this.dataPlate;
        location.description = child.description;
        return location;
      })
    );
  }

  private static toRectInStructImage(points: { x: number; y: number }[] | undefined): RectInStructImage | null {
    if (!points || points.length === 0) return null;

    const minX = Math.min(...points.map((p) => p.x));
    const minY = Math.min(...points.map((p) => p.y));
    const maxX = Math.max(...points.map((p) => p.x));
    const maxY = Math.max(...points.map((p) => p.y));

    const rect = new RectInStructImage();
    rect.left = Math.trunc(minX * 100);
    rect.top = Math.trunc(minY * 100);
    rect.width = Math.trunc((maxX - minX) * 100);
    rect.height = Math.trunc((maxY - minY) * 100);
    return rect;
  }

  private static toSubjectDetail(
    liveAssetDetails: LiveAsset | LiveAssetDetails,
    properties: LiveAssetProperty[] = [],
    labelConfig: LabelConfig = {}
  ) {
    const subject = new SubjectDetail();
    subject.id = liveAssetDetails.id;
    subject.name = liveAssetDetails.name;
    subject.shortName = liveAssetDetails.shortName;
    subject.description = liveAssetDetails.description;
    subject.displayName = liveAssetDetails.displayName;
    subject.serialNo = liveAssetDetails.serialNumber;
    subject.createdBy = liveAssetDetails.createdBy;
    subject.version = 1;
    subject.isValid = true;

    subject.serialNo = this.getProp(properties, this.SystemProperty.SerialNumber) ?? liveAssetDetails.serialNumber;
    subject.owner = this.getProp(properties, this.SystemProperty.Owner, labelConfig.Owner);
    subject.country = this.getProp(properties, this.SystemProperty.Country, labelConfig.Country);
    subject.state = this.getProp(properties, this.SystemProperty.State, labelConfig.State);
    subject.district = this.getProp(properties, this.SystemProperty.Region, labelConfig.Region);
    subject.site = this.getProp(properties, this.SystemProperty.Site, labelConfig.Site);
    subject.project = this.getProp(properties, this.SystemProperty.Project, labelConfig.Project);

    return subject;
  }

  private static getProp(properties: LiveAssetProperty[], name: string, label?: string): string | null {
    const byName = properties.find((p) => p.name === name)?.value;
    if (byName) return byName;
    if (label && label !== name) return properties.find((p) => p.name === label)?.value ?? null;
    return null;
  }
}
